import { useContext, useEffect, useMemo } from 'react';
import moment from 'moment';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';

import { useSelector } from 'react-redux';
import { RootState } from 'redux/schemas';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import {
  REQUEST_DATE_FORMAT,
  MentorRequestSource,
} from 'redux/schemas/models/mentor-request';
import { setMentorRequestCarouselIndex } from 'redux/actions/mentorship-program-connections';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';
import { MentorshipProgramRequest } from 'redux/schemas/models/mentorship-program-connections';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgramEnrollment as getEnrollment } from 'redux/actions/mentoring-program-enrollments';

import Avatar from 'athena/components/mentoring-program/avatar';
import ClickableContainer from 'components/clickable-container';
import MentoringProgramContext from 'athena/components/mentoring-program/context';

import { css } from '@emotion/react';
import { gray2, gray7 } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';

const styles = css`
  :hover {
    background-color: ${gray7};
    border-radius: ${quarterSpacing}px;
  }
  .request-date {
    color: ${gray2};
  }
}`;

export type MentoringRequestProps = MentorshipProgramRequest & {
  index: number;
};

const MentoringRequest = (props: MentoringRequestProps) => {
  const {
    id,
    index,
    createdAt,
    mentorEnrollmentId,
  } = props;

  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { mentoringProgram } = useContext(MentoringProgramContext);

  const mentorEnrollment: MentoringProgramEnrollment = useSelector((state: RootState) => (
    getMentoringProgramEnrollment(state, mentorEnrollmentId)
  ));
  const mentorUser = mentorEnrollment?.user;
  const date = useMemo(() => moment(createdAt).format(REQUEST_DATE_FORMAT) || '', [createdAt]);

  const dataQa = config.pendo.athena.mentorshipProgram.participantHome.requestsPanel.mentorRequestItem;
  const dataQaId = `${dataQa}-${id}`;
  const ariaLabel = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REQUESTS_PANEL.MENTOR_REQUEST_ARIA_LABEL(mentorUser?.fullName);

  const handleClick = () => {
    dispatch(setMentorRequestCarouselIndex(index));
    $state.go('mentoring-program-root', {
      mentorRequest: id,
      source: MentorRequestSource.MENTEE_HOME,
    }, {
      notify: false,
    });
  };

  useEffect(() => {
    if (!mentorUser) {
      wrapThunkAction(
        dispatch(
          getEnrollment({
            programId: mentoringProgram.id,
            enrollmentId: mentorEnrollmentId,
          }),
        ),
      );
    }
  }, [
    dispatch,
    mentoringProgram.id,
    mentorEnrollmentId,
    mentorUser,
  ]);

  return (
    <>
      {mentorUser && (
        <ClickableContainer
          css={styles}
          onClick={handleClick}
          className='d-flex px-4 py-3'
          data-qa={dataQa}
          data-qa-id={dataQaId}
          aria-label={ariaLabel}
        >
          <Avatar
            showNameOnHover
            user={mentorUser}
            key={mentorUser?.id}
            className='mr-1 avatar'
          />
          <div className='request-info ml-3'>
            <div className='name semi-bold'>
              {mentorUser.fullName}
            </div>
            <div className='request-date'>
              {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REQUESTS_PANEL.REQUESTED(date)}
            </div>
          </div>
        </ClickableContainer>
      )}
    </>
  );
};

export default MentoringRequest;
